import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { contentSnapshotService } from '../services/contentSnapshotService';
import type { 
  PerfilRelatorioConfig, 
  ActiveContentInfo,
  InsumosRelatoriosContent 
} from '../services/contentSnapshotService';

interface ProfileWithId extends PerfilRelatorioConfig {
  id: string;
}

interface ContentSnapshotState {
  // Data
  profiles: ProfileWithId[];
  activeContentInfo: ActiveContentInfo | null;
  reportContents: InsumosRelatoriosContent | null;
  
  // Status states
  isLoading: boolean;
  isRefreshing: boolean;
  error: string | null;
  hasAccess: boolean | null; // null = não verificado, true = tem acesso, false = sem acesso
  
  // Metadata
  lastFetch: number | null;
  cacheInfo: {
    activeInfoCached: boolean;
    totalCacheEntries: number;
    lastActiveInfoFetch?: number;
  } | null;

  // Actions
  fetchProfiles: () => Promise<void>;
  fetchActiveInfo: () => Promise<void>;
  refreshProfiles: () => Promise<void>;
  checkAccess: () => Promise<void>;
  getProfileById: (id: string) => ProfileWithId | undefined;
  clearError: () => void;
  clearData: () => void;
  updateCacheInfo: () => void;
}

export const useContentSnapshotStore = create<ContentSnapshotState>()(
  devtools(
    (set, get) => ({
      // Initial state
      profiles: [],
      activeContentInfo: null,
      reportContents: null,
      isLoading: false,
      isRefreshing: false,
      error: null,
      hasAccess: null,
      lastFetch: null,
      cacheInfo: null,

      // Actions
      fetchProfiles: async () => {
        const currentState = get();
        
        // Se já estiver carregando, não inicia nova request
        if (currentState.isLoading) return;
        
        set({ isLoading: true, error: null });

        try {
          // Primeiro verifica se tem acesso
          if (currentState.hasAccess === null) {
            await get().checkAccess();
          }

          // Se não tem acesso, não tenta carregar
          if (!get().hasAccess) {
            set({ 
              isLoading: false,
              profiles: [],
              error: 'Acesso negado. Esta funcionalidade requer permissões administrativas.'
            });
            return;
          }

          // Busca informações da versão ativa
          const activeInfo = await contentSnapshotService.fetchActiveVersionInfo();
          
          // Busca conteúdo dos relatórios
          const reportContents = await contentSnapshotService.fetchReportContents(
            activeInfo.active_version.id
          );

          // Converte perfis para array com IDs
          const profilesArray = Object.entries(reportContents.perfis).map(([id, profile]) => ({
            id,
            ...profile,
          }));

          // Atualiza cache info
          const cacheInfo = contentSnapshotService.getCacheInfo();

          set({
            profiles: profilesArray,
            activeContentInfo: activeInfo,
            reportContents,
            isLoading: false,
            error: null,
            lastFetch: Date.now(),
            cacheInfo,
          });

        } catch (error) {
          console.error('Erro ao buscar perfis do content snapshot:', error);
          
          const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
          
          // Se for erro de permissão, marca como sem acesso
          if (errorMessage.includes('401') || errorMessage.includes('403')) {
            set({ 
              hasAccess: false,
              error: 'Acesso negado. Faça login com uma conta administrativa.',
            });
          } else {
            set({ 
              error: errorMessage,
            });
          }
          
          set({ isLoading: false });
        }
      },

      fetchActiveInfo: async () => {
        try {
          const activeInfo = await contentSnapshotService.fetchActiveVersionInfo();
          const cacheInfo = contentSnapshotService.getCacheInfo();
          
          set({ 
            activeContentInfo: activeInfo,
            cacheInfo,
            error: null,
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
          console.error('Erro ao buscar informações da versão ativa:', error);
          set({ error: errorMessage });
        }
      },

      refreshProfiles: async () => {
        const currentState = get();
        
        // Se já estiver fazendo refresh, não inicia novo
        if (currentState.isRefreshing) return;
        
        set({ isRefreshing: true, error: null });

        try {
          // Força refresh do cache
          await contentSnapshotService.refreshCache();
          
          // Recarrega os dados
          await get().fetchProfiles();
          
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
          console.error('Erro ao fazer refresh dos perfis:', error);
          set({ error: errorMessage });
        } finally {
          set({ isRefreshing: false });
        }
      },

      checkAccess: async () => {
        try {
          const hasAccess = await contentSnapshotService.checkServiceAvailability();
          set({ hasAccess, error: null });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
          console.error('Erro ao verificar acesso:', error);
          
          // Se for erro de conectividade ou outro, mantém como não verificado
          if (errorMessage.includes('401') || errorMessage.includes('403')) {
            set({ hasAccess: false });
          } else {
            set({ 
              hasAccess: false, // Assume sem acesso em caso de erro
              error: 'Erro ao verificar permissões: ' + errorMessage,
            });
          }
        }
      },

      getProfileById: (id: string) => {
        return get().profiles.find(profile => profile.id === id);
      },

      clearError: () => {
        set({ error: null });
      },

      clearData: () => {
        set({
          profiles: [],
          activeContentInfo: null,
          reportContents: null,
          hasAccess: null,
          lastFetch: null,
          cacheInfo: null,
          error: null,
        });
      },

      updateCacheInfo: () => {
        const cacheInfo = contentSnapshotService.getCacheInfo();
        set({ cacheInfo });
      },
    }),
    {
      name: 'content-snapshot-store',
      partialize: (state) => ({
        // Persiste apenas dados básicos, não estados de loading
        lastFetch: state.lastFetch,
        hasAccess: state.hasAccess,
      }),
    }
  )
);

// Hooks utilitários
export const useProfiles = () => {
  const store = useContentSnapshotStore();
  return {
    profiles: store.profiles,
    isLoading: store.isLoading,
    error: store.error,
    fetchProfiles: store.fetchProfiles,
    refreshProfiles: store.refreshProfiles,
  };
};

export const useActiveContentInfo = () => {
  const store = useContentSnapshotStore();
  return {
    activeContentInfo: store.activeContentInfo,
    isLoading: store.isLoading,
    error: store.error,
    fetchActiveInfo: store.fetchActiveInfo,
  };
};

export const useContentAccess = () => {
  const store = useContentSnapshotStore();
  return {
    hasAccess: store.hasAccess,
    checkAccess: store.checkAccess,
    error: store.error,
  };
};

// Seletor para debug
export const useContentSnapshotDebug = () => {
  const store = useContentSnapshotStore();
  return {
    cacheInfo: store.cacheInfo,
    lastFetch: store.lastFetch,
    hasAccess: store.hasAccess,
    profilesCount: store.profiles.length,
    updateCacheInfo: store.updateCacheInfo,
  };
};